// The CHIP grammar: a value from a closed vocabulary (#11426).
//
// Two kinds, and keeping them apart is the point. A STATUS chip carries
// judgement and draws from the five semantic states; a TAG chip carries
// category and draws from the soft categorical ramp. Nothing else in the
// product gets colour.
//
// Styles are inline `var()` references on purpose: this file may not name a
// font, colour or distance, and referencing the tokens directly is what makes
// that checkable (`rawLiterals.test.ts`).

import React from "react";
import { type EnumToneStyles } from "./enumTone";
import { isSemanticState, type SemanticState } from "./semanticState";
import { hueFor } from "./tagHue";
import type { RampHue } from "./hueSnap";

const chipBase: React.CSSProperties = {
  // inline-block, not inline-flex: text-overflow only paints an ellipsis on
  // the box that owns the text, and a flex container never does. Keeping the
  // label a direct child is what lets the chip itself give way in a narrow
  // slot (#11846); the dot below aligns as an inline box instead.
  display: "inline-block",
  verticalAlign: "middle",
  fontFamily: "var(--fp-font-ui)",
  fontSize: "var(--fp-t-fine)",
  fontWeight: "var(--fp-w-strong)" as unknown as number,
  letterSpacing: "var(--fp-track-body)",
  lineHeight: "var(--fp-lh-body)",
  padding: "var(--fp-gap-hair) var(--fp-gap-tight)",
  borderRadius: "var(--fp-chip-r)",
  borderWidth: "1px",
  borderStyle: "solid",
  whiteSpace: "nowrap",
  // A chip may shrink to the slot it sits in; its label then ellipsizes
  // inside the border (#11846). Without these a chip lays out at natural
  // width and a clipping slot slices it mid-glyph, border and all.
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export interface StatusChipProps {
  /** The client's own word for this state — "Full DD", "Wound down". */
  label: string;
  /**
   * Which of the five meanings the label maps onto. Absent means we do not
   * know, which is `none` — never a judgement we did not make.
   */
  state?: SemanticState | null;
  /**
   * A fully resolved tone (enumValueToneStyles) — present when the org
   * configured a color for this value, whose snapped hue then paints the
   * chip (CD org-color ruling). The judgement FORM — border, tint, dot —
   * is this component's either way.
   */
  tone?: EnumToneStyles | null;
}

export const StatusChip: React.FC<StatusChipProps> = ({
  label,
  state,
  tone,
}) => {
  const resolved: SemanticState = isSemanticState(state) ? state : "none";

  return (
    <span
      data-state={tone?.state ?? resolved}
      style={{
        ...chipBase,
        color: tone ? tone.textColor : `var(--fp-st-${resolved})`,
        // Judgement is a bordered, tinted pill with a dot — the mock's
        // `.chip.st` exactly (gaps-v2: Lakshay's mockups override the earlier
        // ink-and-dot ruling; the mock CSS is the binding spec). The BORDER is
        // what tells a status from a tag at a glance: a tag is a borderless
        // wash, a status carries its semantic edge.
        backgroundColor: tone
          ? tone.backgroundColor
          : `var(--fp-st-${resolved}-bg)`,
        borderColor: tone ? tone.borderColor : `var(--fp-st-${resolved}-bd)`,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: "inline-block",
          verticalAlign: "middle",
          width: "var(--fp-dot)",
          height: "var(--fp-dot)",
          borderRadius: "var(--fp-dot)",
          backgroundColor: "currentColor",
          marginRight: "var(--fp-gap-hair)",
        }}
      />
      {label}
    </span>
  );
};

export interface TagChipProps {
  /** The category itself. Its hue is derived from it unless the page assigns one. */
  value: string;
  /**
   * The hue a page assigns from what a person chose, which may be any hue of the ramp (the
   * Dashboards list's view groups wear their groups' colours).
   */
  hue?: RampHue;
}

export const TagChip: React.FC<TagChipProps> = ({ value, hue: assigned }) => {
  const hue = assigned ?? hueFor(value);

  return (
    <span
      data-hue={hue}
      style={{
        ...chipBase,
        color: `var(--fp-tag-${hue})`,
        backgroundColor: `var(--fp-tag-${hue}-bg)`,
        borderColor: "transparent",
      }}
    >
      {value}
    </span>
  );
};
